/**
 * Разоблачение шулера: реагируем на `DIRTY_CAUGHT` / `DIRTY_FALSE_ACCUSE` в
 * `belotArmenian.lastEvents`, синхронизируясь с `belotSync.seq`, чтобы не
 * воспроизводить FX при реконнекте и лишних перерисовках.
 *
 * @module components/online/hooks/useBelotShulerCaughtFx
 */

import { useEffect, useMemo, useRef, useState } from 'react';

/** Длительность X-Ray руки и тостов (мс). */
export const SHULER_REVEAL_MS = 1800;

const THROTTLE_MS_NOTIFY = 380;

/**
 * @typedef {{
 *   token: number,
 *   cheaterSeat: number|null,
 *   accuserSeat: number|null,
 *   card: number|null,
 *   cheaterHand: number[]|null,
 * }} ShulerCaughtFx
 */

/**
 * @typedef {{
 *   token: number,
 *   accuserSeat: number|null,
 *   card: number|null,
 * }} ShulerFalseFx
 */

/**
 * @param {object} args
 * @param {object|null|undefined} args.belotArmenian
 * @param {number} args.belotSyncSeq
 * @param {(id: string, vol?: number) => void} args.play
 * @param {boolean} [args.audioSuppressed]
 * @param {(durationMs: number) => void} [args.onRevealWindowStart] — блок очереди лёгких FX на время X-Ray
 * @returns {{
 *   caught: ShulerCaughtFx | null,
 *   falseAccuse: ShulerFalseFx | null,
 *   animated: boolean,
 * }}
 */
export function useBelotShulerCaughtFx({
  belotArmenian,
  belotSyncSeq,
  play,
  audioSuppressed = false,
  onRevealWindowStart,
}) {
  const [caught, setCaught] = useState(/** @type {ShulerCaughtFx | null} */ (null));
  const [falseAccuse, setFalseAccuse] = useState(/** @type {ShulerFalseFx | null} */ (null));

  const bootstrappedRef = useRef(false);
  const lastSeqRef = useRef(0);
  const tokenRef = useRef(0);
  const clearTimerRef = useRef(/** @type {ReturnType<typeof setTimeout>|null} */ (null));
  const throttleNotifyRef = useRef(0);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  useEffect(
    () => () => {
      if (clearTimerRef.current != null) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    const seq = Number(belotSyncSeq) || 0;

    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      lastSeqRef.current = seq;
      return;
    }

    if (seq <= lastSeqRef.current) return;
    lastSeqRef.current = seq;

    const events = Array.isArray(belotArmenian?.lastEvents) ? belotArmenian.lastEvents : [];
    /** @type {Record<string, unknown>|null} */
    let caughtPayload = null;
    for (let i = events.length - 1; i >= 0; i -= 1) {
      const ev = events[i];
      if (!ev || typeof ev !== 'object') continue;
      if ((/** @type {{ type?: string }} */ (ev)).type !== 'DIRTY_CAUGHT') continue;
      const pl = (/** @type {{ payload?: unknown }} */ (ev)).payload;
      caughtPayload =
        pl && typeof pl === 'object' ? /** @type {Record<string, unknown>} */ (pl) : {};
      break;
    }
    /** @type {Record<string, unknown>|null} */
    let falsePayload = null;
    if (!caughtPayload) {
      for (let i = events.length - 1; i >= 0; i -= 1) {
        const ev = events[i];
        if (!ev || typeof ev !== 'object') continue;
        if ((/** @type {{ type?: string }} */ (ev)).type !== 'DIRTY_FALSE_ACCUSE') continue;
        const pl = (/** @type {{ payload?: unknown }} */ (ev)).payload;
        falsePayload =
          pl && typeof pl === 'object' ? /** @type {Record<string, unknown>} */ (pl) : {};
        break;
      }
    }

    const throttledNotifySound = (soundId, vol = 0.48) => {
      if (audioSuppressed || typeof play !== 'function') return;
      const now = Date.now();
      if (now - throttleNotifyRef.current < THROTTLE_MS_NOTIFY) return;
      throttleNotifyRef.current = now;
      play(soundId, vol);
    };

    if (clearTimerRef.current != null) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }

    if (caughtPayload != null) {
      tokenRef.current += 1;
      const tok = tokenRef.current;
      const cs = Number(caughtPayload.cheaterSeat);
      const as = Number(caughtPayload.accuserSeat);
      const card = Number(caughtPayload.card);
      const rawHand = caughtPayload.cheaterHand;
      let cheaterHand = null;
      if (Array.isArray(rawHand) && rawHand.length > 0) {
        cheaterHand = rawHand.map((x) => Number(x)).filter((n) => Number.isFinite(n));
        if (cheaterHand.length === 0) cheaterHand = null;
      }
      setCaught({
        token: tok,
        cheaterSeat: Number.isFinite(cs) ? cs : null,
        accuserSeat: Number.isFinite(as) ? as : null,
        card: Number.isFinite(card) ? card : null,
        cheaterHand,
      });
      setFalseAccuse(null);
      if (typeof onRevealWindowStart === 'function') {
        try {
          onRevealWindowStart(SHULER_REVEAL_MS);
        } catch {
          /* ignore */
        }
      }
      throttledNotifySound('card-flip', 0.42);
      clearTimerRef.current = setTimeout(() => {
        setCaught(null);
        clearTimerRef.current = null;
      }, SHULER_REVEAL_MS);
      return;
    }

    if (falsePayload != null) {
      tokenRef.current += 1;
      const tok = tokenRef.current;
      const as = Number(falsePayload.accuserSeat);
      const card = Number(falsePayload.card);
      setFalseAccuse({
        token: tok,
        accuserSeat: Number.isFinite(as) ? as : null,
        card: Number.isFinite(card) ? card : null,
      });
      setCaught(null);
      if (typeof onRevealWindowStart === 'function') {
        try {
          onRevealWindowStart(SHULER_REVEAL_MS);
        } catch {
          /* ignore */
        }
      }
      throttledNotifySound('notification', 0.32);
      clearTimerRef.current = setTimeout(() => {
        setFalseAccuse(null);
        clearTimerRef.current = null;
      }, SHULER_REVEAL_MS);
    }
  }, [belotSyncSeq, belotArmenian, audioSuppressed, play, onRevealWindowStart]);

  return {
    caught,
    falseAccuse,
    animated: !prefersReducedMotion,
  };
}
